// ─────────────────────────────────────────────────────────────────────────────
// Canonical Currency Conversion Engine & Exchange Rate Normalizer (Frontend Mirror)
// Single Source of Truth for INR Base Pricing Architecture
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Standard exchange rate orientation:
 * 1 TARGET_CURRENCY = X INR (e.g. 1 USD = 90 INR)
 *
 * Direct Conversion:
 * foreignPrice = inrPrice / exchangeRate
 *
 * Reverse Conversion:
 * inrPrice = foreignPrice * exchangeRate
 */

export interface EffectiveExchangeRateResult {
  effectiveRate: number;
  liveExchangeRate: number;
  manualExchangeRate: number | null;
  isManualOverride: boolean;
  source: 'manual' | 'live' | 'fallback';
}

/**
 * Calculate the effective exchange rate following:
 * effectiveRate = manualExchangeRate ?? liveExchangeRate
 */
export function resolveEffectiveExchangeRate(
  manualRate?: number | string | null,
  liveRate?: number | string | null,
  fallbackRate: number = 1.0
): EffectiveExchangeRateResult {
  const manual =
    typeof manualRate === 'number' && Number.isFinite(manualRate) && manualRate > 0
      ? manualRate
      : manualRate && !isNaN(Number(manualRate)) && Number(manualRate) > 0
      ? Number(manualRate)
      : null;

  const live =
    typeof liveRate === 'number' && Number.isFinite(liveRate) && liveRate > 0
      ? liveRate
      : liveRate && !isNaN(Number(liveRate)) && Number(liveRate) > 0
      ? Number(liveRate)
      : fallbackRate || 1.0;

  const isManualOverride = manual !== null;
  const effectiveRate = isManualOverride ? manual : live;

  return {
    effectiveRate,
    liveExchangeRate: live,
    manualExchangeRate: manual,
    isManualOverride,
    source: isManualOverride ? 'manual' : liveRate ? 'live' : 'fallback',
  };
}

/**
 * Convert an INR base price to a foreign target currency using the full precision rate.
 *
 * Formula: foreignPrice = inrPrice / rate
 */
export function convertInrToForeign(
  inrPrice: number | string | null | undefined,
  exchangeRate: number | string | null | undefined,
  decimals: number = 2
): number {
  const baseInr = Number(inrPrice) || 0;
  const rate = Number(exchangeRate);
  if (!Number.isFinite(rate) || rate <= 0) return baseInr;

  // Calculate using full precision before rounding final result
  const rawForeign = baseInr / rate;
  const factor = Math.pow(10, decimals);
  return Math.round(rawForeign * factor) / factor;
}

/**
 * Convert a foreign currency price back to INR.
 *
 * Formula: inrPrice = foreignPrice * rate
 */
export function convertForeignToInr(
  foreignPrice: number | string | null | undefined,
  exchangeRate: number | string | null | undefined,
  decimals: number = 2
): number {
  const foreign = Number(foreignPrice) || 0;
  const rate = Number(exchangeRate);
  if (!Number.isFinite(rate) || rate <= 0) return foreign;

  const rawInr = foreign * rate;
  const factor = Math.pow(10, decimals);
  return Math.round(rawInr * factor) / factor;
}

/**
 * Format currency with proper symbol and precision.
 */
export function formatCurrencyAmount(
  amount: number | string | null | undefined,
  currency: string = 'INR',
  decimals: number = 2
): string {
  const val = Number(amount) || 0;
  const code = String(currency || 'INR').toUpperCase();
  const symbols: Record<string, string> = {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
    AED: 'AED ',
    SAR: 'SAR ',
    CAD: 'CA$',
    SGD: 'S$',
    JPY: '¥',
  };
  const sym = symbols[code] || `${code} `;
  const formatted = val.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${sym}${formatted}`;
}
